import axios from 'axios';
import { apiRequest } from '../authConfig';
import { notifyAccessEnded } from '../utils/accessEnded';   // CEV-S3

// Environment-based API configuration (allow override via env)
const getApiBaseUrl = () => {
  const fromEnv = process.env.REACT_APP_API_BASE_URL;
  if (fromEnv && fromEnv.trim().length > 0) {
    const normalized = fromEnv.replace(/\/$/, '') + '/api';
    if (normalized.startsWith('http://localhost:5022')) {
      console.warn('[ApiService] Overriding http dev base URL to HTTPS to avoid CORS redirect issues.');
      return 'https://localhost:7040/api';
    }
    return normalized;
  }
  if (process.env.NODE_ENV === 'production') {
    // In production, use same-origin relative base. SPA and API share the App Service.
    return '/api';
  }
  // Default to HTTPS dev backend to avoid CORS redirect issues on preflight
  return 'https://localhost:7040/api';
};

const API_BASE_URL = getApiBaseUrl();

class ApiService {
    constructor() {
        this.api = axios.create({
            baseURL: API_BASE_URL,
            headers: {
                'Content-Type': 'application/json',
            },
        });
        // CEV-S3 — show the access-ended notice for every client of this service
        this.api.interceptors?.response?.use((r) => r, (e) => { notifyAccessEnded(e); return Promise.reject(e); });
    }

    setAuthToken(token) {
        console.log('apiService.setAuthToken called with:', token ? token.substring(0, 50) + '...' : 'null');
        if (token) {
            this.api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            console.log('Authorization header set in apiService:', this.api.defaults.headers.common['Authorization'].substring(0, 50) + '...');
        } else {
            delete this.api.defaults.headers.common['Authorization'];
        }
    }

    // Generic HTTP methods
    async get(url) {
        const response = await this.api.get(url);
        return response.data;
    }

    async post(url, data) {
        const response = await this.api.post(url, data);
        return response.data;
    }

    async put(url, data) {
        const response = await this.api.put(url, data);
        return response.data;
    }

    async delete(url) {
        const response = await this.api.delete(url);
        return response.data;
    }

    // Test endpoints
    async getPublicData() {
        const response = await this.api.get('/TestPermissions/public');
        return response.data;
    }

    async getAuthenticatedData() {
        const response = await this.api.get('/TestPermissions/authenticated');
        return response.data;
    }

    async getEmployeesData() {
        const response = await this.api.get('/TestPermissions/view-employees');
        return response.data;
    }

    async getManagerData() {
        const response = await this.api.get('/TestPermissions/manage-employees');
        return response.data;
    }

    async getAdminData() {
        const response = await this.api.get('/TestPermissions/admin-only');
        return response.data;
    }

    async getDebugToken() {
        const response = await this.api.get('/TestPermissions/debug-token');
        return response.data;
    }

    // Role management endpoints
    async getRoles() {
        const response = await this.api.get('/Role');
        return response.data;
    }

    async createRole(roleData) {
        const response = await this.api.post('/Role', roleData);
        return response.data;
    }

    async updateRole(id, roleData) {
        const response = await this.api.put(`/Role/${id}`, roleData);
        return response.data;
    }

    async deleteRole(id) {
        const response = await this.api.delete(`/Role/${id}`);
        return response.data;
    }

    async assignRoleToUser(roleId, userId) {
        const response = await this.api.post(`/Role/${roleId}/users/${userId}`);
        return response.data;
    }

    async revokeRoleFromUser(roleId, userId) {
        const response = await this.api.delete(`/Role/${roleId}/users/${userId}`);
        return response.data;
    }

    async getRolePermissions(roleId) {
        const response = await this.api.get(`/Role/${roleId}/permissions`);
        return response.data;
    }

    async grantPermissionToRole(roleId, permission) {
        const response = await this.api.post(`/Role/${roleId}/permissions`, { permission });
        return response.data;
    }

    async revokePermissionFromRole(roleId, permission) {
        const response = await this.api.delete(`/Role/${roleId}/permissions/${permission}`);
        return response.data;
    }

    async seedDefaultRoles() {
        const response = await this.api.post('/Role/seed');
        return response.data;
    }

    // User profile endpoints
    async getUserProfile() {
        const response = await this.api.get('/auth/profile');
        return response;
    }

    async getCurrentUser() {
        const response = await this.api.get('/auth/me');
        return response;
    }

    // Employee endpoints
    async getEmployees() {
        const response = await this.api.get('/Employees');
        return response;
    }

    async getEmployee(id) {
        const response = await this.api.get(`/Employees/${id}`);
        return response;
    }

    async createEmployee(employeeData) {
        const response = await this.api.post('/Employees', employeeData);
        return response;
    }

    async updateEmployee(id, employeeData) {
        const response = await this.api.put(`/Employees/${id}`, employeeData);
        return response;
    }

    async deleteEmployee(id) {
        const response = await this.api.delete(`/Employees/${id}`);
        return response;
    }
}

export default new ApiService();
